
import React, { PropsWithChildren } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { useWidthContext } from '../../providers/Width';

import './Image.css';
import { IMarginControl, IPaddingControl } from '../../types/shared/SpacingControl';
import { getMarginFromProps, getPaddingFromProps, wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';

interface IImageProps extends MappedComponentProperties, IPaddingControl, IMarginControl {
    imageUrlDesktop?: string;
    imageUrlMobile?: string;
    imageCentered?: boolean;
    imageBottomAligned?: boolean;
    imageAltTextDesktop?: string;
    imageAltTextMobile?: string;
    imageIdDesktop?: string;
    imageIdMobile?: string;
    imageTitle?: string;
    imageCaption?: string;
    imageDescription?: string;
    imageLink?: string;
    newTabLink?: boolean;
    imageHeightDesktop?: string;
    imageWidthDesktop?: string;
    imageHeightMobile?: string;
    imageWidthMobile?: string;
    preloadDesktopImage?: boolean;
    lazyLoad?: boolean;
}

interface IImageContentProps extends Pick<IImageProps, | 'imageLink' | 'newTabLink' | 'imageBottomAligned' | 'preloadDesktopImage' | 'imageUrlDesktop' | 'lazyLoad'> {
    isMobile: boolean;
    imageUrl?: string;
    imageAlt?: string;
    imageId?: string;
    imageRestricted: boolean;
}

export const ImageEditConfig = {
    emptyLabel: 'Image',
    isEmpty: (props: PropsWithChildren<IImageProps>) => {
        return !props || !(props.imageUrlDesktop && props.imageUrlMobile && props.imageAltTextDesktop && props.imageAltTextMobile);
    },
};

const ImageContent = (props: IImageContentProps): JSX.Element => {
    const lazyLoad = props.lazyLoad && !props.preloadDesktopImage;
    const theImage = () => {
        return (
            <img
                className={`${props.imageRestricted ? 'ImageComponent__container__content--restricted' : ''} ${props.imageBottomAligned ? 'ImageComponent__container__content--bottom-align' : ''}`}
                src={props.imageUrl}
                {...(lazyLoad && { loading: 'lazy' })}
                fetchpriority='high'
                alt={props.imageAlt}
                id={props.imageId}
            />
        );
    };
    return (
        <>
            {props.preloadDesktopImage && props.imageUrlDesktop &&
                <img className='hidden' src={props.imageUrlDesktop} />
            }

            {props.imageLink ?
                <a
                    href={props.imageLink}
                    target={`${props.newTabLink ? '_blank' : ''}`}
                >
                    {theImage()}
                </a>
                :
                theImage()}
        </>
    );
};

const ImageComponent = (props: PropsWithChildren<IImageProps>): JSX.Element => {
    const { width } = useWidthContext();
    const isMobile = width <= 768;
    const imageWidth = isMobile ? props.imageWidthMobile : props.imageWidthDesktop;
    const imageHeight = isMobile ? props.imageHeightMobile : props.imageHeightDesktop;
    const imageUrl = isMobile ? props.imageUrlMobile : props.imageUrlDesktop;
    const imageAlt = isMobile ? props.imageAltTextMobile : props.imageAltTextDesktop;
    const imageId = isMobile ? props.imageIdMobile : props.imageIdDesktop;

    const padding = getPaddingFromProps(props, width);
    const margin = getMarginFromProps(props, width);


    return (
        <div id='image-component' className={'ImageComponent'} style={{ ...margin }}>
            <div className={'ImageComponent__container'}>
                <div
                    className={`ImageComponent__container__content${props.imageCentered ? '--centered' : ''}`}
                    style={{
                        width: imageWidth,
                        height: imageHeight,
                        ...padding,
                        ...margin,
                    }}
                >
                    <ImageContent
                        {...props}
                        isMobile={isMobile}
                        imageUrl={imageUrl}
                        imageAlt={imageAlt}
                        imageId={imageId}
                        imageRestricted={(imageWidth !== undefined || imageHeight !== undefined)}
                    />
                </div>

                {(props.imageTitle || props.imageDescription || props.imageCaption) && (
                    <div className={'ImageComponent__container__description'}>
                        {props.imageTitle && (
                            <h5 className={'ImageComponent__container__description__text'}>
                                {transformHTMLParserLinks(wrapTrademarkSuperscript(props.imageTitle))}
                            </h5>
                        )}

                        {props.imageDescription && (
                            <p className={'ImageComponent__container__description__text'}>
                                {transformHTMLParserLinks(wrapTrademarkSuperscript(props.imageDescription))}
                            </p>
                        )}

                        {props.imageCaption && (
                            <p className={'ImageComponent__container__description__text'}>
                                {transformHTMLParserLinks(wrapTrademarkSuperscript(props.imageCaption))}
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImageComponent;
